import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import styles from '../App.module.css';

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || Boolean(nav.standalone);
}

export function InstallAppCard() {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [dismissed, setDismissed] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const standalone = useMemo(() => isStandalone(), []);
  const ios = useMemo(() => isIos(), []);

  useEffect(() => {
    try {
      if (localStorage.getItem('yorisoi-install-dismissed') === '1') setDismissed(true);
    } catch {
      // ignore
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (standalone || dismissed) return null;

  return (
    <section className={styles.installCard} aria-labelledby="install-title">
      <div className={styles.installHeader}>
        <h2 id="install-title" className={styles.installTitle}>
          {ui.install.title}
        </h2>
        <button
          type="button"
          className={styles.ghostButton}
          onClick={() => {
            setDismissed(true);
            try {
              localStorage.setItem('yorisoi-install-dismissed', '1');
            } catch {
              // ignore
            }
          }}
        >
          {ui.install.dismiss}
        </button>
      </div>
      <p className={styles.installBody}>{ui.install.body}</p>
      {ios ? (
        <ol className={styles.installSteps}>
          <li>{ui.install.iosStep1}</li>
          <li>{ui.install.iosStep2}</li>
          <li>{ui.install.iosStep3}</li>
        </ol>
      ) : (
        <div className={styles.installActions}>
          {deferred ? (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={async () => {
                await deferred.prompt();
                setDeferred(null);
              }}
            >
              {ui.install.androidButton}
            </button>
          ) : (
            <p className={styles.installHint}>{ui.install.androidHint}</p>
          )}
        </div>
      )}
      <p className={styles.installNote}>{ui.install.note}</p>
    </section>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
